import { Stack, Typography } from '@mui/material';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { SearchMdIcon } from '@/components/icons/SearchMdIcon';
import { CopyResultPopover } from '@/modules/pdf/DocumentSearch/CopyResultButton';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

export interface DocumentChatCitationFooterProps {
  currentIndex: number;
  totalCount: number;
  onNavigate: (direction: number) => void;
  onCopy: () => void;
  onLocate?: () => void;
  searchResponse?: string;
  withCitationCopy?: string;
  enableCopyCitationPopover?: boolean;
}

export function DocumentChatCitationFooter({
  currentIndex,
  totalCount,
  onNavigate,
  onCopy,
  onLocate,
  searchResponse,
  withCitationCopy,
  enableCopyCitationPopover,
}: DocumentChatCitationFooterProps) {
  const hasCitations = totalCount > 0;
  const shouldUsePopover =
    hasCitations &&
    searchResponse &&
    withCitationCopy &&
    enableCopyCitationPopover;

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        py: 0.5,
        borderRadius: 1,
      }}
    >
      {shouldUsePopover ? (
        <CopyResultPopover
          searchResponse={searchResponse}
          withCitationCopy={withCitationCopy}
          buttonWithPopoverProps={{
            startIcon: Copy07Icon,
            popperProps: {
              disablePortal: true,
              PaperProps: {
                sx: {
                  zIndex: zIndexes.TOOLTIP,
                },
              },
            },
          }}
          hideLabel
        />
      ) : (
        <IconButton
          icon={Copy07Icon}
          size="xs"
          variant="transparent"
          onClick={onCopy}
          ariaLabel="Copy message"
        />
      )}

      {hasCitations && (
        <Stack direction="row" spacing={0.75} alignItems="center">
          {onLocate && (
            <IconButton
              icon={SearchMdIcon}
              size="xs"
              variant="transparent"
              onClick={onLocate}
              ariaLabel="Locate in document"
              sx={{
                backgroundColor: COLORS.TEAL[50],
                border: `1px solid ${COLORS.TEAL[100]}`,
                borderRadius: '4px',
                padding: '4px',
                '&:hover': {
                  backgroundColor: COLORS.TEAL[100],
                },
                '& svg': {
                  color: COLORS.TEAL[600],
                },
              }}
            />
          )}

          <Typography
            variant="body-xs"
            sx={{
              color: 'text.default.secondary',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            Reference {currentIndex + 1} of {totalCount}
          </Typography>

          {totalCount > 1 && (
            <Stack direction="row" spacing={0}>
              <IconButton
                icon={ChevronLeftIcon}
                size="xs"
                variant="transparent"
                onClick={() => onNavigate(-1)}
                ariaLabel="Previous reference"
              />
              <IconButton
                icon={ChevronRightIcon}
                size="xs"
                variant="transparent"
                onClick={() => onNavigate(1)}
                ariaLabel="Next reference"
              />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
